// AB#1910 — ISO 4217 currency codes offered by a `currency`-type field's select. A modest, common set
// (extend as needed); the EditField keeps any stored value that isn't in the list so data is never lost.
//
// AB#2516 — a bare code reads poorly in a picker, so each carries its symbol and the option renders as
// "GBP (£)". The STORED VALUE is still the bare ISO code — this is presentation only. Currencies whose
// symbol is just the code again carry none, and fall back to the code alone rather than reading
// "CHF (CHF)" or showing empty brackets.

export interface CurrencyOption {
    code: string
    /** The currency's symbol. Omitted where there is no distinct glyph worth showing. */
    symbol?: string
}

export const CURRENCY_OPTIONS: readonly CurrencyOption[] = [
    { code: 'GBP', symbol: '£' },
    { code: 'USD', symbol: '$' },
    { code: 'EUR', symbol: '€' },
    { code: 'CHF' },
    { code: 'SEK', symbol: 'kr' },
    { code: 'NOK', symbol: 'kr' },
    { code: 'DKK', symbol: 'kr' },
    { code: 'PLN', symbol: 'zł' },
    { code: 'CZK', symbol: 'Kč' },
    { code: 'HUF', symbol: 'Ft' },
    { code: 'RON', symbol: 'lei' },
    { code: 'BGN', symbol: 'лв' },
    { code: 'AUD', symbol: '$' },
    { code: 'NZD', symbol: '$' },
    { code: 'CAD', symbol: '$' },
    { code: 'JPY', symbol: '¥' },
    { code: 'CNY', symbol: '¥' },
    { code: 'HKD', symbol: '$' },
    { code: 'SGD', symbol: '$' },
    { code: 'INR', symbol: '₹' },
    { code: 'ZAR', symbol: 'R' },
    { code: 'AED', symbol: 'د.إ' },
    { code: 'SAR', symbol: '﷼' },
    { code: 'BRL', symbol: 'R$' },
    { code: 'MXN', symbol: '$' },
]

/** The codes alone, in the same order — the pre-AB#2516 shape, kept for callers that only need codes. */
export const CURRENCIES: string[] = CURRENCY_OPTIONS.map(c => c.code)

const SYMBOL_BY_CODE = new Map(CURRENCY_OPTIONS.map(c => [c.code, c.symbol]))

/**
 * The label shown for a currency code: `GBP (£)`, or the bare code where there is no distinct symbol
 * or the code is not one we ship — a stored value outside the list is never hidden or relabelled.
 */
export function currencyLabel(code: string): string {
    const symbol = SYMBOL_BY_CODE.get(code)
    return symbol && symbol !== code ? `${code} (${symbol})` : code
}
